<template>
    <div class="years">
        <div
            v-for="year in years"
            :key="year"
            ref="yearDivs"
            class="year"
            :class="getClassYear(year)"
            @click="pick(year)"
        >
            {{ year }}
        </div>
    </div>
</template>

<script lang="ts">
import type { PropType, Ref } from 'vue'
import { ref, defineComponent, onMounted } from 'vue'

export default defineComponent({
    name: 'VCalendarMonth',

    props: {
        modelValue: {
            type: Object as PropType<number | null>,
            required: true,
        },
        yearMin: {
            type: Number,
            required: false,
            default: 1971,
        },
        yearMax: {
            type: Number,
            required: false,
            default: 2050,
        },
    },
    emits: {
        ['pick']: (payload: number) => payload,
    },
    setup(props, { emit }) {
        const years = ref([]) as Ref<number[]>

        for (let i = 1971; i <= 2050; i += 1) {
            years.value.push(i)
        }

        const getIsOutOfRange = (year: number) =>
            year < props.yearMin || year > props.yearMax

        const pick = (year: number) => {
            if (getIsOutOfRange(year)) {
                return
            }

            emit('pick', year)
        }

        const yearDivs = ref([]) as Ref<HTMLElement[]>

        onMounted(() => {
            const scrollToYear = props.modelValue || new Date().getFullYear()
            const scrollToYearIndex = years.value.indexOf(scrollToYear)

            if (scrollToYearIndex !== -1) {
                yearDivs.value[scrollToYearIndex]?.scrollIntoView({
                    block: 'center',
                    inline: 'start',
                })
            }
        })

        const getClassYear = (year: number) =>
            year === props.modelValue
                ? 'picked'
                : getIsOutOfRange(year)
                  ? 'disabled'
                  : ''

        return {
            years,
            pick,
            yearDivs,
            getClassYear,
        }
    },
})
</script>

<style lang="scss" scoped>
.years {
    position: absolute;
    top: 42px;
    padding: 0 16px;
    background-color: var(--white);
    width: 100%;
    height: calc(100% - 42px);
    overflow-y: auto;
    display: flex;
    flex-wrap: wrap;

    .year {
        position: relative;
        width: 88px;
        height: 48px;
        cursor: pointer;
        user-select: none;
        z-index: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        &.picked {
            color: var(--white);

            &::after {
                position: absolute;
                inset: 0;
                margin: auto;
                width: 89px;
                height: 44px;
                content: '';
                z-index: -1;
                background-color: var(--green);
            }
        }

        &.disabled {
            pointer-events: none;
            color: var(--grey-300);
        }
    }
}
</style>
